import { BomStructure } from '../../../../models/bom-structure';

export interface Order {
  id: number;
  createdAt: string;
  orderNumber: string;
  name: string;
  status: OrderStatus;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  deadline: string | null;
  /**
   * Progress counted in parts (0112): every node of the trees, root included, weighs one
   * whatever its unit. doneLineCount counts the fully reported ones — the rule Done is
   * derived from — and progressFraction (0..1) is the mean of each line's reported share,
   * so it is 1 exactly when the order is Done.
   */
  lineCount: number;
  doneLineCount: number;
  progressFraction: number;
  /**
   * Root items only — the positions the order page lists (0036). Not the tree-wide
   * lineCount. The detail endpoint does not carry it.
   */
  itemCount: number;
  /**
   * The order's faces (0081): every photographed root, in item order, distinct by
   * attachment — the board card shows the first, a click flips to the next (0085). The detail endpoint does not carry it.
   */
  photos: OrderPhoto[];
  items: OrderItem[];
}

/** One face of an order: a root's photo and that root's name, for the tooltip. */
export interface OrderPhoto {
  attachmentId: number;
  itemName: string;
}

export enum OrderStatus {
  new,
  planned,
  inProgress,
  done
}

// the orders board's column names, per status — the one label map for zlecenia
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  [OrderStatus.new]: 'Nowe',
  [OrderStatus.planned]: 'Zaplanowano',
  [OrderStatus.inProgress]: 'W trakcie',
  [OrderStatus.done]: 'Zakończono'
};

/**
 * The header a planner edits on the order page — name and deadline travel together
 * (PatchProductionOrder takes the whole header each time).
 */
export interface OrderHeader {
  name: string;
  deadline: string | null;
}

/**
 * What CreateProductionOrder answers with: the header of the new order. Planning
 * dates, deadline and totals are not there — a fresh order has none.
 */
export interface CreatedOrder {
  id: number;
  createdAt: string;
  orderNumber: string;
  name: string;
  status: OrderStatus;
  items: OrderItem[];
}

export interface OrderItemAllocation {
  clientOrderId: number;
  clientOrderNumber: string;
  clientName: string;
  amount: number;
}

export interface OrderItem {
  orderItemId?: number;
  bomItemId: number | null;
  name: string;
  /**
   * Copied from the BOM item at snapshot time, like the name — amount and perParentAmount
   * are expressed in it (0069).
   */
  unit: string;
  /**
   * Quantity of this item the order needs — the ordered quantity on a root,
   * multiplied down the tree on children.
   */
  amount: number;
  /**
   * Per-one-parent recipe quantity copied from the BOM; null on a root.
   */
  perParentAmount: number | null;
  /**
   * Frozen on the line at snapshot time (0115) — PODSUMOWANIE counts a purchased line as
   * the material and skips what is under it (0120).
   */
  structure: BomStructure;
  /**
   * Pegging (0014): set on a client-sourced root only — which zamówienie the item covers.
   * Absent on the create responses, which never carry it.
   */
  allocation?: OrderItemAllocation | null;
  /**
   * The photo frozen on the node at creation, else its BOM item's current one; null when
   * neither (0080/0081). Absent on the create responses, which never carry it.
   */
  primaryPhotoAttachmentId?: number | null;
  children: OrderItem[]; // TODO: instead of re-using OrderItem make two distinct models: OrderItem (for top-level item) and OrderItemNode (for children nodes) or sth?
}
